import { Link } from 'react-router';
import {
  shortDigest,
  type Approval,
  type Engagement,
  type ReportMeta,
} from '@ai-secure-twin/shared';
import { api, ApiError } from '../../lib/api';
import { useAcrossEngagements } from '../../lib/useAcrossEngagements';

type ReportStanding = 'Published' | 'Awaiting approval' | 'Declined' | 'Superseded';

/**
 * Where a report stands, from the decisions recorded about it. A report is published only by an
 * approval naming its version; everything else is either waiting, refused, or replaced.
 */
function standing(
  report: ReportMeta,
  engagement: Engagement,
  latestVersion: number,
  approvals: readonly Approval[],
): ReportStanding {
  const decision = approvals
    .filter((a) => a.gate === 'REPORT' && a.reportVersion === report.version)
    .sort((a, b) => a.at.localeCompare(b.at))
    .at(-1);
  if (decision?.decision === 'APPROVED') return 'Published';
  if (decision?.decision === 'DECLINED') return 'Declined';
  if (report.version === latestVersion && engagement.stage === 'REPORT_PENDING_APPROVAL') {
    return 'Awaiting approval';
  }
  return 'Superseded';
}

const CLS: Record<ReportStanding, string> = {
  Published: 'status-done',
  'Awaiting approval': 'status-waiting',
  Declined: 'status-active',
  Superseded: 'status-muted',
};

/**
 * Every report across the tenant. A report is generated when an engagement is submitted for its
 * Report gate: a fixed snapshot of the evidence, built by code and stored under its own hash.
 */
export function ReportsPage() {
  const reports = useAcrossEngagements<ReportMeta>('reports', api.listReports);
  const approvals = useAcrossEngagements<Approval>('approvalHistory', api.listApprovals);

  const rows = reports.items
    .map(({ item, engagement }) => {
      const mine = reports.items.filter((r) => r.engagement.id === engagement.id);
      const latest = Math.max(...mine.map((r) => r.item.version));
      const decided = approvals.items
        .filter((a) => a.engagement.id === engagement.id)
        .map((a) => a.item);
      return { report: item, engagement, standing: standing(item, engagement, latest, decided) };
    })
    .sort((a, b) => b.report.generatedAt.localeCompare(a.report.generatedAt));

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <span className="eyebrow">Workspace</span>
          <h1>Reports</h1>
        </div>
      </div>

      <p className="muted" style={{ maxWidth: '72ch', marginTop: -8 }}>
        Technical reports, generated from engagement evidence by code — not written by the agent.
        Each is stored under its SHA-256, and its approver's decision records that hash, so what was
        published can be checked against what was approved.
      </p>

      {reports.isPartial || approvals.isPartial ? (
        <p className="demo-notice" role="status">
          Some engagements could not be read, so this list may be incomplete.
        </p>
      ) : null}

      <section className="card panel table-wrap">
        {reports.isLoading || approvals.isLoading ? (
          <p className="muted">Loading reports…</p>
        ) : reports.isError ? (
          <p className="login-error" role="alert">
            {reports.error instanceof ApiError ? reports.error.message : 'Could not load reports.'}
          </p>
        ) : rows.length === 0 ? (
          <div className="empty">
            <p>No reports yet.</p>
            <p className="muted">
              A report is generated when an engagement in triage is submitted for approval, once
              every finding has a decision.
            </p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Engagement</th>
                <th>Report</th>
                <th>Generated</th>
                <th>Findings</th>
                <th>Status</th>
                <th>Digest</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ report, engagement, standing: s }) => (
                <tr key={`${engagement.id}:${report.version}`}>
                  <td>
                    <Link to={`/app/engagements/${engagement.id}`} className="table-link">
                      {engagement.name}
                    </Link>
                  </td>
                  <td>
                    <Link
                      to={`/app/engagements/${engagement.id}/reports/${report.version}`}
                      className="table-link"
                    >
                      v{report.version} · technical
                    </Link>
                  </td>
                  <td className="muted">{new Date(report.generatedAt).toLocaleString()}</td>
                  <td>{report.findingCount}</td>
                  <td>
                    <span className={`status ${CLS[s]}`}>{s}</span>
                  </td>
                  <td className="mono muted" style={{ fontSize: 12 }}>
                    {shortDigest(report.digest)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
